import React, { useState, useEffect, useRef } from 'react'
import {
  Bell,
  Check,
  CheckCheck,
  Info,
  AlertTriangle,
  CheckCircle,
  AlertCircle,
  Trash2,
  ArrowRight
} from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { apiService } from '../../services/api'
import { useAuth } from '../../context/AuthContext'

const NotificationDropdown = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedTab, setSelectedTab] = useState('all')
  const dropdownRef = useRef(null)
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { user } = useAuth()

  const { data: notificationsData, isLoading } = useQuery(
    ['notifications', selectedTab],
    () => apiService.get(`/notifications?${selectedTab === 'unread' ? 'unreadOnly=true&' : ''}limit=5`).then(res => res.data),
    {
      refetchInterval: isOpen ? 60000 : 600000,
      staleTime: 30000,
      retry: 1,
      refetchOnWindowFocus: false,
      onError: (error) => console.warn('Failed to fetch notifications:', error.message)
    }
  )

  const { data: unreadData } = useQuery(
    'notifications-unread-count',
    () => apiService.get('/notifications/unread-count').then(res => res.data),
    {
      refetchInterval: 600000,
      staleTime: 30000,
      retry: 1,
      refetchOnWindowFocus: false,
      onError: (error) => console.warn('Failed to fetch unread count:', error.message)
    }
  )

  const markAsReadMutation = useMutation(
    (notificationId) => apiService.patch(`/notifications/${notificationId}/read`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('notifications')
        queryClient.invalidateQueries('notifications-unread-count')
      }
    }
  )

  const markAllAsReadMutation = useMutation(
    () => apiService.patch('/notifications/mark-all-read'),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('notifications')
        queryClient.invalidateQueries('notifications-unread-count')
      }
    }
  )

  const deleteNotificationMutation = useMutation(
    (notificationId) => apiService.delete(`/notifications/${notificationId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('notifications')
        queryClient.invalidateQueries('notifications-unread-count')
      }
    }
  )

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const getNotificationLink = (notification) => {
    const data = notification.data
      ? (typeof notification.data === 'string' ? JSON.parse(notification.data) : notification.data)
      : {}
    const role = user?.role
    const title = notification.title.toLowerCase()
    const message = notification.message.toLowerCase()
    const actionUrl = data.actionUrl || ''

    const extractId = (url) => {
      const match = url.match(/\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i)
      return match ? match[1] : null
    }

    if (
      title.includes('edit request') || message.includes('edit request') ||
      notification.type === 'edit_request' ||
      actionUrl.includes('/requests')
    ) {
      const id = data.requestId || extractId(actionUrl)
      if (role === 'admin') return id ? `/admin/requests?open=${id}` : '/admin/requests'
      return id ? `/client/requests?open=${id}` : '/client/requests'
    }

    if (
      title.includes('support') || message.includes('support') ||
      notification.type === 'support' || notification.type === 'info' ||
      actionUrl.includes('/support')
    ) {
      const id = data.ticketId || extractId(actionUrl)
      if (role === 'admin') return id ? `/admin/support?open=${id}` : '/admin/support'
      return id ? `/client/support?open=${id}` : '/client/support'
    }

    if (actionUrl && !actionUrl.includes('localhost:3000') && !actionUrl.includes('dashboard')) {
      return actionUrl
    }

    if (title.includes('site published') || message.includes('published') || notification.type === 'site_published') {
      if (role === 'admin') return data.siteId ? `/admin/sites/${data.siteId}` : '/admin/sites'
      return data.siteId ? `/client/sites/${data.siteId}` : '/client/sites'
    }

    if (
      title.includes('payment') || message.includes('payment') ||
      title.includes('billing') || notification.type === 'billing'
    ) {
      if (role === 'admin') return data.clientId ? `/admin/billing?client=${data.clientId}` : '/admin/billing'
      return '/client/billing'
    }

    if (title.includes('backup') || message.includes('backup') || notification.type === 'backup_completed') {
      if (role === 'admin') return data.backupId ? `/admin/backups?highlight=${data.backupId}` : '/admin/backups'
    }

    if (notification.type === 'user_activity') {
      if (role === 'admin') return data.userId ? `/admin/users/${data.userId}` : '/admin/users'
    }

    return role === 'admin' ? '/admin' : '/client'
  }

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) markAsReadMutation.mutate(notification.id)
    const link = getNotificationLink(notification)
    setIsOpen(false)
    if (link.startsWith('http')) {
      window.open(link, '_blank')
    } else {
      navigate(link)
    }
  }

  const getNotificationIcon = (type) => {
    const iconStyle = { width: '1rem', height: '1rem' }
    switch (type) {
      case 'success': return <CheckCircle style={{ ...iconStyle, color: 'var(--success)' }} />
      case 'warning': return <AlertTriangle style={{ ...iconStyle, color: 'var(--warning)' }} />
      case 'error':   return <AlertCircle style={{ ...iconStyle, color: 'var(--danger)' }} />
      default:        return <Info style={{ ...iconStyle, color: 'var(--accent)' }} />
    }
  }

  const getIconToneName = (type) => {
    switch (type) {
      case 'success': return 'success'
      case 'warning': return 'warning'
      case 'error':   return 'danger'
      default:        return ''
    }
  }

  const getTimeAgo = (timestamp) => {
    const diff = Date.now() - new Date(timestamp).getTime()
    const mins = Math.floor(diff / 60000)
    const hrs = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)
    if (mins < 1) return 'Just now'
    if (mins < 60) return `${mins}m ago`
    if (hrs < 24) return `${hrs}h ago`
    if (days < 7) return `${days}d ago`
    return new Date(timestamp).toLocaleDateString()
  }

  const notifications = notificationsData?.notifications || []
  const unreadCount = unreadData?.count || 0

  return (
    <div style={{ position: 'relative' }} ref={dropdownRef}>
      {/* Bell button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="btn btn-ghost btn-icon"
        style={{ position: 'relative', borderRadius: '0.75rem' }}
      >
        <Bell style={{ width: '1.25rem', height: '1.25rem' }} />
        {unreadCount > 0 && (
          <>
            <span style={{
              position: 'absolute',
              top: '-0.25rem',
              right: '-0.25rem',
              background: 'var(--danger)',
              color: '#fff',
              fontSize: '0.65rem',
              fontWeight: 700,
              borderRadius: '9999px',
              minWidth: '1.125rem',
              height: '1.125rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 0.25rem',
              border: '2px solid var(--surface)',
              zIndex: 10,
            }}>
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
            <span style={{
              position: 'absolute',
              top: '-0.25rem',
              right: '-0.25rem',
              background: 'var(--danger)',
              borderRadius: '9999px',
              minWidth: '1.125rem',
              height: '1.125rem',
              animation: 'ping 1s cubic-bezier(0,0,0.2,1) infinite',
              opacity: 0.6,
            }} />
          </>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="card" style={{
          position: 'absolute',
          right: 0,
          top: 'calc(100% + 0.75rem)',
          width: '380px',
          maxWidth: '92vw',
          zIndex: 60,
          boxShadow: 'var(--shadow-lg)',
          overflow: 'hidden',
        }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>Notifications</h3>
            {unreadCount > 0 && <span className="badge badge-accent" style={{ fontSize: 10 }}>{unreadCount} new</span>}
            {/* All/Unread tabs */}
            <div style={{ display: 'flex', gap: 4, marginLeft: 4 }}>
              {['all', 'unread'].map(tab => (
                <button key={tab} onClick={() => setSelectedTab(tab)} style={{ padding: '3px 10px', fontSize: 12, fontWeight: 500, borderRadius: 6, border: 'none', cursor: 'pointer', background: selectedTab === tab ? 'var(--surface-2)' : 'transparent', color: selectedTab === tab ? 'var(--text)' : 'var(--text-muted)', textTransform: 'capitalize' }}>
                  {tab}
                </button>
              ))}
            </div>
            {unreadCount > 0 && (
              <button onClick={() => markAllAsReadMutation.mutate()} disabled={markAllAsReadMutation.isLoading} className="btn btn-sm btn-ghost" style={{ marginLeft: 'auto', fontSize: 11 }}>
                <CheckCheck style={{ width: 12, height: 12 }} /> Mark all read
              </button>
            )}
          </div>

          {/* Notification list */}
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {isLoading ? (
              <div style={{ padding: '2.5rem 1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem' }} className="text-muted">
                <div style={{
                  width: '1rem',
                  height: '1rem',
                  border: '2px solid var(--accent)',
                  borderTopColor: 'transparent',
                  borderRadius: '50%',
                  animation: 'spin 0.7s linear infinite',
                }} />
                <span style={{ fontSize: '0.875rem' }}>Loading…</span>
              </div>
            ) : notifications.length === 0 ? (
              <div style={{ padding: '2.5rem 1.5rem', textAlign: 'center' }}>
                <div style={{
                  width: '3rem',
                  height: '3rem',
                  margin: '0 auto 0.75rem',
                  background: 'var(--bg-1)',
                  borderRadius: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <Bell style={{ width: '1.5rem', height: '1.5rem', color: 'var(--text-faint)' }} />
                </div>
                <p className="font-medium" style={{ fontSize: '0.875rem' }}>No notifications</p>
                <p className="text-faint" style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>
                  {selectedTab === 'unread' ? "You're all caught up!" : "We'll notify you when something happens."}
                </p>
              </div>
            ) : (
              <div>
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className="cursor-pointer"
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.75rem',
                      padding: !notification.is_read ? '0.875rem 1rem 0.875rem calc(1rem - 3px)' : '0.875rem 1rem',
                      borderBottom: '1px solid var(--border)',
                      borderLeft: !notification.is_read ? '3px solid var(--accent)' : '3px solid transparent',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-1)'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}
                  >
                    {/* Icon */}
                    <span className={`icon-tile ${getIconToneName(notification.type)}`} style={{ width: 32, height: 32, fontSize: 14, flexShrink: 0, marginTop: 2 }}>
                      {getNotificationIcon(notification.type)}
                    </span>

                    {/* Content */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="flex items-start justify-between gap-2">
                        <p className={['font-semibold truncate', notification.is_read ? 'text-muted' : ''].filter(Boolean).join(' ')}
                           style={{ fontSize: '0.875rem', lineHeight: '1.25' }}>
                          {notification.title}
                        </p>
                        {!notification.is_read && (
                          <span style={{
                            flexShrink: 0,
                            width: '0.5rem',
                            height: '0.5rem',
                            marginTop: '0.25rem',
                            borderRadius: '50%',
                            background: 'var(--accent)',
                          }} />
                        )}
                      </div>
                      <p className="text-muted" style={{ fontSize: '0.75rem', marginTop: '0.125rem', lineHeight: '1.5' }}>
                        {notification.message}
                      </p>
                      <div className="flex items-center justify-between" style={{ marginTop: '0.375rem' }}>
                        <span className="text-faint" style={{ fontSize: '0.75rem' }}>
                          {getTimeAgo(notification.created_at)}
                        </span>
                        <div className="flex items-center gap-1" style={{ opacity: 0 }}
                             onMouseEnter={e => e.currentTarget.style.opacity = '1'}
                             onMouseLeave={e => e.currentTarget.style.opacity = '0'}>
                          {!notification.is_read && (
                            <button
                              onClick={(e) => { e.stopPropagation(); markAsReadMutation.mutate(notification.id) }}
                              style={{ padding: '0.25rem', borderRadius: '0.25rem', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}
                              title="Mark as read"
                            >
                              <Check style={{ width: '0.875rem', height: '0.875rem' }} />
                            </button>
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); deleteNotificationMutation.mutate(notification.id) }}
                            style={{ padding: '0.25rem', borderRadius: '0.25rem', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}
                            title="Delete"
                          >
                            <Trash2 style={{ width: '0.875rem', height: '0.875rem' }} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div style={{ padding: '0.75rem 1rem', borderTop: '1px solid var(--border)' }}>
              <button
                className="btn btn-ghost"
                style={{ width: '100%', borderRadius: 0, padding: '12px', color: 'var(--accent-3)' }}
                onClick={() => {
                  setIsOpen(false)
                  navigate(user?.role === 'admin' ? '/admin/notifications' : '/client/notifications')
                }}
              >
                View all notifications <ArrowRight style={{ width: 13, height: 13 }} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default NotificationDropdown
